/* Pure progress selectors over the authored path.
 *
 * A chapter opens once what it assumes is finished. The prerequisites are
 * declared in data/routes.ts, so the order drawn on the map is advice rather
 * than a queue; the bonus stage sits outside the meter.
 */
import { EDGES, EXTRA_EDGES, NODES } from '../data/nodes';
import { PREREQ } from '../data/routes';
import { SRS } from '../data/srs';
import { isDue, pRecall } from './srs';
import type { Block, Edge, NodeDef, NodeState, RevRow, SavedState } from '../types';

export const ALL_EDGES: Edge[] = [...EDGES, ...EXTRA_EDGES];

export function byId(id: string): NodeDef | null {
  return NODES.find((n) => n.id === id) ?? null;
}

export function idxOf(id: string): number {
  return NODES.findIndex((n) => n.id === id);
}

export function core(): NodeDef[] {
  return NODES.filter((n) => !n.extra);
}

export function extras(): NodeDef[] {
  return NODES.filter((n) => n.extra);
}

export function optionalStages(): NodeDef[] {
  return NODES.filter((n) => n.extra && !n.bonus);
}

export function bonusNode(): NodeDef | null {
  return NODES.find((n) => n.bonus) ?? null;
}

/* What a chapter actually assumes — the gate, as opposed to the drawn order. */
export function parents(id: string): string[] {
  return PREREQ[id] ?? [];
}

export function children(id: string): NodeDef[] {
  return ALL_EDGES.filter((e) => e[0] === id)
    .map((e) => byId(e[1]))
    .filter((n): n is NodeDef => !!n);
}

/* 01 for a chapter, 01+ for an optional stage hanging off it. */
export function pad(node: NodeDef | null): string {
  if (!node) return '';
  if (node.extra) {
    const p = byId(node.parent ?? '');
    return (p ? pad(p) : 'B') + '+';
  }
  return ('0' + (idxOf(node.id) + 1)).slice(-2);
}

export function blockOf(blockId: string): Block | null {
  for (const n of NODES) {
    const b = n.blocks.find((x) => x.id === blockId);
    if (b) return b;
  }
  return null;
}

export function nodeOfBlock(blockId: string): NodeDef | null {
  return NODES.find((n) => n.blocks.some((b) => b.id === blockId)) ?? null;
}

export function checkIndex(node: NodeDef): number {
  const i = node.blocks.findIndex((b) => b.kind === 'check');
  return i < 0 ? node.blocks.length - 1 : i;
}

export function checkIdOf(id: string): string | null {
  const n = byId(id);
  if (!n) return null;
  return n.blocks.find((b) => b.kind === 'check')?.id ?? null;
}

/* ── block + chapter completion ───────────────────────────────────── */
export function blockDone(b: Block, s: SavedState): boolean {
  return b.kind === 'check' ? !!s.chk[b.id] : !!s.done[b.id];
}

export function chapterDone(n: NodeDef, s: SavedState): boolean {
  return n.blocks.every((b) => blockDone(b, s));
}

export function progress(n: NodeDef, s: SavedState): number {
  return n.blocks.filter((b) => blockDone(b, s)).length;
}

export function curBlock(n: NodeDef, s: SavedState): number {
  const c = s.cur[n.id];
  if (typeof c !== 'number' || c < 0 || c >= n.blocks.length) return firstUndone(n, s);
  return c;
}

export function firstUndone(n: NodeDef, s: SavedState): number {
  const i = n.blocks.findIndex((b) => !blockDone(b, s));
  return i < 0 ? n.blocks.length - 1 : i;
}

export function stateOf(n: NodeDef, s: SavedState): NodeState {
  if (chapterDone(n, s)) return 'done';
  const p = parents(n.id);
  if (!p.length) return 'available';
  for (const pid of p) {
    const parent = byId(pid);
    if (!parent || !chapterDone(parent, s)) return 'locked';
  }
  return 'available';
}

export function unlockNeighbours(n: NodeDef, s: SavedState): NodeDef[] {
  return children(n.id).filter((c) => stateOf(c, s) === 'available');
}

/* ── review queue ─────────────────────────────────────────────────── */
export function srsOf(id: string, s: SavedState): RevRow | null {
  const r = s.rev[id];
  const c = checkIdOf(id);
  if (!r || !c || !s.chk[c]) return null;
  return r;
}

export function dueList(s: SavedState, now = Date.now()): NodeDef[] {
  return NODES.filter((n) => !n.bonus && srsOf(n.id, s) && isDue(srsOf(n.id, s)!, now)).sort(
    (a, b) => pRecall(srsOf(a.id, s), now) - pRecall(srsOf(b.id, s), now),
  );
}

/* ── the meter ────────────────────────────────────────────────────── */
export function xpOf(n: NodeDef): number {
  return n.blocks.reduce((sum, b) => sum + b.xp, 0);
}

export function totalXP(): number {
  return NODES.filter((n) => !n.bonus).reduce((sum, n) => sum + xpOf(n), 0);
}

export function earnedXP(s: SavedState): number {
  return NODES.filter((n) => !n.bonus).reduce(
    (sum, n) => sum + n.blocks.reduce((x, b) => (blockDone(b, s) ? x + b.xp : x), 0),
    0,
  );
}

export function totalBlocks(): number {
  return NODES.filter((n) => !n.bonus).reduce((c, n) => c + n.blocks.length, 0);
}

export function doneBlocks(s: SavedState): number {
  return NODES.filter((n) => !n.bonus).reduce((c, n) => c + progress(n, s), 0);
}

export function firstOpen(s: SavedState): NodeDef {
  const c = core();
  const available = c.find((n) => stateOf(n, s) === 'available');
  if (available) return available;
  return c.find((n) => !chapterDone(n, s)) ?? c[0];
}

/* How long ago a chapter was last reviewed, as data — the component formats it
   with the active language's "today" / " d" wording. */
export type DueWhen = { kind: 'today' } | { kind: 'days'; days: number };

export function dueWhen(id: string, s: SavedState, now = Date.now()): DueWhen | null {
  const r = srsOf(id, s);
  if (!r) return null;
  const days = Math.round((now - r.ts) / SRS.DAY);
  return days < 1 ? { kind: 'today' } : { kind: 'days', days };
}
